import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Clock, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { formatUsd } from "@/lib/marketplace/queries";
import {
  acceptMarketplaceDelivery,
  cancelMarketplaceOrder,
  deliverMarketplaceOrder,
  getMarketplaceOrder,
  requestOrderRevision,
  reviewMarketplaceOrder,
  submitOrderRequirements,
} from "@/lib/marketplace/orders.functions";
import { DisputePanel } from "@/components/marketplace/dispute-panel";
import { useT } from "@/i18n";


export const Route = createFileRoute("/marketplace/order/$orderId")({
  head: () => ({
    meta: [
      { title: "Order | BeHot.Now Marketplace" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OrderPage,
});

function OrderPage() {
  const { t } = useT();
  const { orderId } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["mp-order", orderId],
    queryFn: () => getMarketplaceOrder({ data: { orderId } }),
    retry: false,
  });

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [rating, setRating] = useState(5);
  const [reviewBody, setReviewBody] = useState("");

  const refresh = () => qc.invalidateQueries({ queryKey: ["mp-order", orderId] });
  const fail = (e: unknown) => toast.error(e instanceof Error ? e.message : t("mp.order.toast_generic_error"));

  const submitReqs = useMutation({
    mutationFn: () =>
      submitOrderRequirements({
        data: { orderId, answers: Object.entries(answers).map(([id, answer]) => ({ id, answer })) },
      }),
    onSuccess: () => { toast.success(t("mp.order.toast_requirements_sent")); void refresh(); },
    onError: fail,
  });
  const deliver = useMutation({
    mutationFn: () => deliverMarketplaceOrder({ data: { orderId, message } }),
    onSuccess: () => { toast.success(t("mp.order.toast_delivery_sent")); setMessage(""); void refresh(); },
    onError: fail,
  });
  const revise = useMutation({
    mutationFn: () => requestOrderRevision({ data: { orderId, reason } }),
    onSuccess: () => { toast.success(t("mp.order.toast_revision_requested")); setReason(""); void refresh(); },
    onError: fail,
  });
  const accept = useMutation({
    mutationFn: () => acceptMarketplaceDelivery({ data: { orderId } }),
    onSuccess: () => { toast.success(t("mp.order.toast_completed")); void refresh(); },
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: () => cancelMarketplaceOrder({ data: { orderId } }),
    onSuccess: () => { toast.success(t("mp.order.toast_cancelled")); void refresh(); },
    onError: fail,
  });
  const review = useMutation({
    mutationFn: () => reviewMarketplaceOrder({ data: { orderId, rating, body: reviewBody } }),
    onSuccess: () => { toast.success(t("mp.order.toast_review_thanks")); void refresh(); },
    onError: fail,
  });

  if (isLoading) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-3xl px-4 py-10">
          <Skeleton className="h-40 w-full" />
        </div>
      </MarketplaceGate>
    );
  }

  if (error || !data) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold">{t("mp.order.not_available")}</h1>
          <Button asChild variant="hot" className="mt-6">
            <Link to="/marketplace/orders">{t("mp.order.back_to_orders")}</Link>
          </Button>
        </div>
      </MarketplaceGate>
    );
  }

  const { order, role, requirements, deliveries, service, buyer, seller } = data;
  const counterpart = role === "buyer" ? seller : buyer;
  const pendingRequirements = order.status === "awaiting_requirements";

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/marketplace/orders" className="text-sm text-muted-foreground hover:underline">
          ← {t("mp.order.all_orders")}
        </Link>

        <div className="card-surface mt-4 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-xl font-bold">{service?.title ?? t("mp.order.title_fallback")}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {role === "buyer" ? t("mp.common.seller") : t("mp.common.buyer")}: {counterpart?.display_name || counterpart?.handle || "—"}
              </p>
            </div>
            <Badge variant="secondary" className="capitalize">
              {order.status.replace(/_/g, " ")}
            </Badge>
          </div>

          <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">{t("mp.order.total")}</dt>
              <dd className="font-display font-bold">{formatUsd(order.total_cents)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t("mp.order.delivery")}</dt>
              <dd className="flex items-center gap-1">
                <Clock className="size-3.5" /> {t("mp.order.days", { n: order.delivery_days })}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t("mp.order.revisions")}</dt>
              <dd>
                {t("mp.order.revisions_used", { used: order.revisions_used, total: order.revisions_included })}
              </dd>
            </div>
          </dl>
        </div>

        {pendingRequirements && requirements.length > 0 && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">
              {role === "buyer" ? t("mp.order.answer_questions") : t("mp.order.waiting_requirements")}
            </h2>
            <div className="mt-4 space-y-4">
              {requirements.map((r) => (
                <div key={r.id}>
                  <p className="text-sm font-semibold">{r.prompt}</p>
                  {role === "buyer" ? (
                    <Textarea
                      className="mt-2"
                      value={answers[r.id] ?? r.answer ?? ""}
                      onChange={(e) => setAnswers((s) => ({ ...s, [r.id]: e.target.value }))}
                    />
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">{r.answer || "—"}</p>
                  )}
                </div>
              ))}
            </div>
            {role === "buyer" && (
              <Button variant="hot" className="mt-4" disabled={submitReqs.isPending} onClick={() => submitReqs.mutate()}>
                {t("mp.order.send_requirements")}
              </Button>
            )}
          </section>
        )}

        {role === "seller" && ["active", "revision_requested", "late"].includes(order.status) && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">{t("mp.order.deliver_work")}</h2>
            <Textarea
              className="mt-3"
              placeholder={t("mp.order.deliver_placeholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Button variant="hot" className="mt-3" disabled={message.length < 3 || deliver.isPending} onClick={() => deliver.mutate()}>
              {t("mp.order.send_delivery")}
            </Button>
          </section>
        )}

        {deliveries.length > 0 && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">{t("mp.order.deliveries")}</h2>
            <ul className="mt-3 space-y-3">
              {deliveries.map((d) => (
                <li key={d.id} className="rounded-lg border border-border p-4 text-sm">
                  <p className="whitespace-pre-line">{d.message}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {new Date(d.delivered_at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {role === "buyer" && order.status === "delivered" && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">{t("mp.order.accept_or_revise")}</h2>
            <Button variant="hot" className="mt-3" disabled={accept.isPending} onClick={() => accept.mutate()}>
              <CheckCircle2 className="mr-2 size-4" /> {t("mp.order.accept_release")}
            </Button>
            <Textarea
              className="mt-4"
              placeholder={t("mp.order.revise_placeholder")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button variant="outline" className="mt-3" disabled={reason.length < 3 || revise.isPending} onClick={() => revise.mutate()}>
              {t("mp.order.request_revision")}
            </Button>
          </section>
        )}

        {role === "buyer" && ["awaiting_requirements"].includes(order.status) && (
          <Button variant="ghost" className="mt-6" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {t("mp.order.cancel_refund")}
          </Button>
        )}

        {role === "buyer" && order.status === "completed" && !data.review && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">{t("mp.order.leave_review")}</h2>
            <div className="mt-3 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} aria-label={t("mp.order.stars_label", { n })}>
                  <Star className={`size-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                </button>
              ))}
            </div>
            <Textarea className="mt-3" value={reviewBody} onChange={(e) => setReviewBody(e.target.value)} />
            <Button variant="hot" className="mt-3" disabled={review.isPending} onClick={() => review.mutate()}>
              {t("mp.order.publish_review")}
            </Button>
          </section>
        )}

        {data.review && (
          <section className="card-surface mt-6 p-6">
            <h2 className="font-display text-lg font-bold">{t("mp.order.your_review")}</h2>
            <div className="mt-2 flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`size-4 ${i < data.review!.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
              ))}
            </div>
            {data.review.body && <p className="mt-2 text-sm text-muted-foreground">{data.review.body}</p>}
          </section>
        )}

        {!["draft", "awaiting_payment", "cancelled", "refunded"].includes(order.status) && (
          <div className="mt-6">
            <DisputePanel orderId={order.id} />
          </div>
        )}
      </div>

    </MarketplaceGate>
  );
}
